import React, { useState } from "react";
import { toast } from "sonner";
import { Download, Share2, Loader2, Wand2 } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { BANNER_FONTS, BANNER_THEMES } from "../../lib/bannerStyle";

const lbl = "text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]";

export function BannerStyleCard({ w, set }) {
  const st = w.banner_style || {};
  const put = (p) => set({ banner_style: { ...st, ...p } });
  const sel = (key, label) => (
    <label className="block"><span className={lbl}>{label}</span>
      <select value={st[key] || ""} onChange={(e) => put({ [key]: e.target.value || null, theme: "custom" })} className="input-luxe mt-1" style={{ fontFamily: st[key] ? `'${st[key]}'` : undefined }} data-testid={`style-${key}`}>
        <option value="">Default</option>
        {BANNER_FONTS.map((f) => <option key={f} value={f} style={{ fontFamily: `'${f}'` }}>{f}</option>)}
      </select>
    </label>
  );
  return (
    <div className="space-y-3" data-testid="banner-style-card">
      <p className="text-sm font-medium">Banner text style <span className="text-xs text-[var(--muted)]">· match your invitations</span></p>
      <div className="flex flex-wrap gap-2">
        {BANNER_THEMES.map((t) => (
          <button key={t.theme} onClick={() => set({ banner_style: { ...t, label: undefined } })} data-testid={`style-theme-${t.theme}`}
            className={`px-3 py-2 rounded-xl border text-left transition-colors ${st.theme === t.theme ? "border-[var(--gold)] bg-[var(--bg-warm)]" : "border-[var(--line)] hover:border-[var(--gold)]"}`}>
            <span className="block text-[9px] uppercase tracking-[0.2em]" style={{ fontFamily: `'${t.headline_font}'` }}>{t.label}</span>
            <span className="block text-xl leading-tight" style={{ fontFamily: `'${t.names_font}'` }}>Ana &amp; Leo</span>
          </button>
        ))}
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        {sel("headline_font", "Headline font")}
        {sel("names_font", "Names font")}
        {sel("subtitle_font", "Subtitle font")}
      </div>
      <div className="flex flex-wrap gap-4 items-center">
        <label className="flex items-center gap-2 text-xs"><input type="color" value={st.color || "#ffffff"} onChange={(e) => put({ color: e.target.value, theme: "custom" })} className="w-9 h-9 rounded cursor-pointer" data-testid="style-color" /> Names & subtitle colour</label>
        <label className="flex items-center gap-2 text-xs"><input type="color" value={st.accent || "#ffffff"} onChange={(e) => put({ accent: e.target.value, theme: "custom" })} className="w-9 h-9 rounded cursor-pointer" data-testid="style-accent" /> Headline colour</label>
        {w.banner_style && <button onClick={() => set({ banner_style: null })} className="text-xs underline" data-testid="style-reset">Reset to default</button>}
      </div>
      <p className="text-xs text-[var(--muted)]">Press Save to publish your style.</p>
    </div>
  );
}

const LAYOUTS = [["classic", "Full photo"], ["framed", "Framed arch"], ["editorial", "Editorial"]];

export function SaveTheDateCard() {
  const [layout, setLayout] = useState("classic");
  const [line, setLine] = useState("");
  const [img, setImg] = useState(null);
  const [busy, setBusy] = useState(false);
  async function make() {
    setBusy(true);
    try {
      const { data } = await api.post("/weddings/mine/save-the-date", { layout, line }, { responseType: "blob" });
      setImg({ blob: data, url: URL.createObjectURL(data) });
    } catch (e) {
      const msg = e.response?.data instanceof Blob ? JSON.parse(await e.response.data.text()).detail : formatApiError(e);
      toast.error(msg);
    } finally { setBusy(false); }
  }
  async function share() {
    const file = new File([img.blob], "save-the-date.png", { type: "image/png" });
    if (navigator.canShare?.({ files: [file] })) { try { await navigator.share({ files: [file], title: "Save the date" }); } catch { /* cancelled */ } }
    else toast.message("Sharing isn't supported here. Download it and share from your phone.");
  }
  return (
    <div className="space-y-3 border-t border-[var(--line)] pt-5" data-testid="save-the-date-card">
      <p className="text-sm font-medium">Save-the-date card <span className="text-xs text-[var(--muted)]">· made from your banner photo, names and date</span></p>
      <div className="flex flex-wrap gap-2">
        {LAYOUTS.map(([id, l]) => <button key={id} onClick={() => setLayout(id)} data-testid={`std-layout-${id}`} className={`px-4 py-2 rounded-full text-xs transition-colors ${layout === id ? "bg-[#111] text-white" : "border border-[var(--line)] hover:border-[var(--gold)]"}`}>{l}</button>)}
      </div>
      <input value={line} onChange={(e) => setLine(e.target.value)} maxLength={90} placeholder="Your line, e.g. Napa Valley · Invitation to follow" className="input-luxe" data-testid="std-line" />
      <button onClick={make} disabled={busy} className="btn-gold text-xs" data-testid="std-make">{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />} Make my card</button>
      {img && (
        <div className="flex flex-col sm:flex-row gap-4 items-start">
          <img src={img.url} alt="Save the date" className="w-56 rounded-xl shadow-lg" data-testid="std-image" />
          <div className="flex flex-col gap-2">
            <a href={img.url} download="save-the-date.png" className="btn-outline-gold text-xs" data-testid="std-download"><Download className="w-3 h-3" /> Download</a>
            <button onClick={share} className="btn-outline-gold text-xs" data-testid="std-share"><Share2 className="w-3 h-3" /> Share</button>
          </div>
        </div>
      )}
    </div>
  );
}
